import React, { useMemo, useState } from 'react';
import Modal from 'src/components/common/ui/Modal';

export type WbsViewAs = 'DATE' | 'WEEK';
export type WbsEvViewMode = 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE';

interface AsOfSelectorProps {
    viewAs: WbsViewAs;
    baseDate: Date;

    onChangeViewAs: (v: WbsViewAs) => void;
    onChangeBaseDate: (d: Date) => void;

    weekOfISO?: string;
    anchorDowLabel?: string;

    evViewMode: WbsEvViewMode;
    onChangeEvViewMode: (v: WbsEvViewMode) => void;
    liveAvailable?: boolean;
}
export const AsOfSelector: React.FC<AsOfSelectorProps> = ({
    viewAs,
    baseDate,
    onChangeViewAs,
    onChangeBaseDate,
    weekOfISO,
    anchorDowLabel,
    evViewMode,
    onChangeEvViewMode,
    liveAvailable = false,
}) => {
    const [helpOpen, setHelpOpen] = useState(false);

    const evModeLabel = useMemo(() => {
        if (evViewMode === 'LIVE') return '실시간';
        if (evViewMode === 'CONFIRMED_W1') return '확정(W-1주)';
        return '확정(W-2주)';
    }, [evViewMode]);

    return (
        <>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                    {/* 기준 토글: 현재는 WEEK만 노출 */}
                    <div className="inline-flex rounded-md border bg-white">
                        {/*
                        <button
                            type="button"
                            className={`px-3 py-1 text-sm ${
                                viewAs === 'DATE' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => onChangeViewAs('DATE')}
                        >
                            날짜 기준
                        </button>
                        */}
                        <button
                            type="button"
                            className={`px-3 py-1 text-sm ${
                                viewAs === 'WEEK' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => onChangeViewAs('WEEK')}
                        >
                            주차 기준
                        </button>
                    </div>

                    {/* EV 표시 기준 */}
                    <div className="inline-flex flex-wrap rounded-md border bg-white">
                        <button
                            type="button"
                            className={`px-3 py-1 text-sm ${
                                evViewMode === 'CONFIRMED_W2'
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => onChangeEvViewMode('CONFIRMED_W2')}
                        >
                            확정(W-2주)
                        </button>
                        <button
                            type="button"
                            className={`px-3 py-1 text-sm ${
                                evViewMode === 'CONFIRMED_W1'
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            onClick={() => onChangeEvViewMode('CONFIRMED_W1')}
                        >
                            확정(W-1주)
                        </button>
                        <button
                            type="button"
                            disabled={!liveAvailable}
                            title={
                                liveAvailable
                                    ? '이번 주 입력 중인 EV를 반영합니다.'
                                    : '실시간은 현재 주차에서만 사용할 수 있습니다.'
                            }
                            className={`px-3 py-1 text-sm ${
                                evViewMode === 'LIVE'
                                    ? 'bg-gray-900 text-white'
                                    : liveAvailable
                                      ? 'text-gray-700 hover:bg-gray-100'
                                      : 'text-gray-400 cursor-not-allowed bg-gray-50'
                            }`}
                            onClick={() => {
                                if (!liveAvailable) return;
                                onChangeEvViewMode('LIVE');
                            }}
                        >
                            실시간
                        </button>
                    </div>

                    <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold text-gray-600 hover:bg-gray-100"
                        onClick={() => setHelpOpen(true)}
                        title="표시 기준 설명"
                        aria-label="표시 기준 설명"
                    >
                        ?
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600 lg:justify-end">
                    <span className="min-w-0">
                        기준 주{anchorDowLabel ? `(${anchorDowLabel})` : ''}: <strong>{weekOfISO ?? '-'}</strong>
                    </span>
                    <span className="hidden text-gray-300 lg:inline">|</span>
                    <span className="min-w-0">
                        EV 표시: <strong>{evModeLabel}</strong>
                    </span>
                </div>
            </div>

            <Modal open={helpOpen} onClose={() => setHelpOpen(false)} title="WBS EV 표시 기준 안내" size="xl">
                <div className="space-y-4 text-sm text-gray-700 leading-6">
                    <div>
                        이 화면은 <strong>주차(Week) 기준</strong>으로 WBS의 계획(PV)과 실적(EV)을 확인합니다.
                    </div>

                    <div className="rounded-lg border bg-gray-50 p-3">
                        <div className="font-semibold text-gray-900">확정(W-2주)</div>
                        <div className="mt-1">
                            ◦ 현재 열려 있는 주차를 기준으로 2주 전까지 확정된 실적을 보여줍니다.<br/>
                            ◦ EV는 승인 완료된 값만 반영합니다.<br/>
                            ◦ PV도 같은 기준 주차로 계산됩니다.
                        </div>
                    </div>

                    <div className="rounded-lg border bg-gray-50 p-3">
                        <div className="font-semibold text-gray-900">확정(W-1주)</div>
                        <div className="mt-1">
                            ◦ 현재 열려 있는 주차를 기준으로 지난 주 실적을 보여줍니다.<br/>
                            ◦ 승인된 항목은 확정값을, 아직 승인 중인 항목은 제출된 잠정값을 반영합니다.<br/>
                            ◦ 일부 항목이 아직 승인 전이면 화면에 안내 메시지가 함께 표시됩니다.
                        </div>
                    </div>

                    <div className="rounded-lg border bg-gray-50 p-3">
                        <div className="font-semibold text-gray-900">실시간</div>
                        <div className="mt-1">
                            ◦ 현재 열려 있는 주차의 기준요일을 기준으로 표시합니다.<br/>
                            ◦ PV는 프로젝트 설정에 지정된 기준요일 기준 계획값으로 계산됩니다.<br/>
                              ※ 예: 오늘이 수요일이어도 기준요일이 금요일이면, 이번 주 금요일 기준 PV로 표시됩니다.<br/>
                            ◦ EV는 W-1 확정/잠정 상태에 이번 주 입력값을 반영한 잠정값으로 표시됩니다.
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                            반려(REJECTED) 상태의 값은 실적으로 보지 않고 직전 확정값을 유지합니다.
                        </div>
                    </div>

                    {/* <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                        실시간 보기는 <strong>현재 주차 + 현재 버전</strong>에서만 의미가 있으므로, 그 외 주차에서는
                        비활성화됩니다.
                    </div> */}
                </div>
            </Modal>
        </>
    );
};

export default AsOfSelector;
